import * as feedActions from '../actions/feed.actions';
import * as widgetActions from '../actions/widget.actions';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AnyFilterItem, findFilter } from '../reducers/feed.reducer';
import {
  CompanyCategory,
  DuplicateObjectErrorItem,
  FeedRemovedItem,
  FilterItem,
} from '../models/core.models';
import { EMPTY, Observable, from, of } from 'rxjs';
import {
  FeedService,
  FilterItemResolveResponse,
  QueryValidationError,
  QueryValidationSuccess,
} from '../services/feed.service';
import { NomObjectClass, NomObjectTypes, WidgetType } from '../constants';
import {
  ViewId,
  ViewPreset,
  ViewPresetService,
} from '@services/view-preset.service';
import {
  applyDefaultPeriod,
  periodChanged,
  selectPreviousPeriod,
  toggleDarkModeSuccess,
  togglePeriodLockedSuccess,
} from '../actions/core.actions';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  skip,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  getContentFilters,
  selectCurrentObject,
  selectFeed,
  selectFeedActiveFilterItems,
  selectFeedFilters,
  selectFeedSettings,
  selectFeedViewConfig,
  selectObjectById,
  selectUser,
  selectWidgetEditMode,
  selectWidgets,
} from '../reducers';
import {
  loadUserCategories,
  loadUserCategoriesFail,
  loadUserCategoriesSuccess,
} from '../actions/post.actions';
import {
  saveObjectExportSettingsState,
  saveObjectOutputSettingsStateFail,
  saveObjectOutputSettingsStateSuccess,
} from '../actions/object.actions';
import { submitFormFailure, submitFormSuccess } from '../actions/form.actions';

import { AlertService } from '../services/alert.service';
import { EditorFormEntity } from '../models/form.models';
import { FeedSettingsService } from '../services/feed-settings.service';
import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { NomenclatureService } from '../services/nomenclature.service';
import { OutputSettingsService } from '../services/output-settings.service';
import { PostService } from '../services/post.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { UserAccessLevel } from '../services/user.service';
import { WidgetSettingsService } from '../services/widget-settings.service';
import { concatLatestFrom } from '@ngrx/operators';
import isEqual from 'lodash-es/isEqual';
import { loadWidget } from '../actions/widget.actions';
import { DialogService } from '@sui/dialog';
import { BusinessConstraintsInfoDialogComponent } from '../components/popups/business-constraints-info-dialog/business-constraints-info-dialog.component';

// Replace with the actual path to your service

@Injectable()
export class FeedEffects {
  /**
   * Load list of posts for a feed
   */
  loadFeedPosts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.loadFeedPosts),
      withLatestFrom(this.store.select(getContentFilters)),
      switchMap(([action, contentFilters]) =>
        this.feedService.getFeedPosts(action.feedId, contentFilters).pipe(
          map((resultResponse) =>
            feedActions.loadFeedPostsSuccess({
              posts: resultResponse.posts,
              totalItemsCount: resultResponse.count_posts,
            }),
          ),
          catchError((error) => of(feedActions.loadFeedPostsFail({ error }))), // Here, you can dispatch a failure action if you want
        ),
      ),
    ),
  );

  /**
   * Load list of filters for a feed
   */
  loadFeedFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.loadFeedFilters),
      mergeMap((action) =>
        this.feedService
          .getFeedFilters(action.feedId, NomObjectClass.FEED)
          .pipe(
            map((response) =>
              feedActions.loadFeedFiltersSuccess({
                filters: response.available,
              }),
            ),
            catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
          ),
      ),
    ),
  );

  /**
   * Load feed data
   */
  loadFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.loadFeed),
      withLatestFrom(this.store.select(selectUser)),
      //filter(([_, user]) => !!user), // Ensure user exists
      switchMap(([action, user]) =>
        this.feedService.getFeed(user.id, action.feedId).pipe(
          map((feed) =>
            feedActions.loadFeedSuccess({
              feed,
              setCurrentFeed: action.setCurrentFeed,
            }),
          ),
          catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
        ),
      ),
    ),
  );

  loadFeedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.loadFeedSuccess),
        tap((action) => {
          if (action.setCurrentFeed) {
            this.store.dispatch(
              feedActions.fetchFeedViewConfig({ feedId: action.feed.id }),
            );
          }
        }),
      ),
    { dispatch: false }, // Mark as non-dispatching effect
  );

  fetchFeedViewConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.fetchFeedViewConfig),
      withLatestFrom(
        this.store.select(selectFeed),
        this.store.select(selectUser),
      ),
      switchMap(([action, feed, user]) =>
        this.viewPresetService
          .getViewPresetByContext(
            action.feedId,
            NomObjectClass.FEED,
            ViewId.PaneViewFeedResults,
          )
          .pipe(
            switchMap((viewPreset) =>
              from(
                this.feedSettings.getFeedConfig(feed.object_type_id, user),
              ).pipe(
                map((defaultViewConfig) => {
                  const isSettingsEmpty =
                    !viewPreset.settings ||
                    (typeof viewPreset.settings === 'object' &&
                      Object.keys(viewPreset.settings).length === 0);

                  const viewConfig = isSettingsEmpty
                    ? defaultViewConfig
                    : viewPreset.settings;

                  // Do not show post completeness score if user has lower access level than Global Admin
                  if (user.access_level_id < UserAccessLevel.GLOBAL_ADMIN) {
                    defaultViewConfig = defaultViewConfig.filter(
                      (item) => item.propertyUri !== 'post_completeness_score',
                    );
                  }

                  return feedActions.fetchFeedViewConfigSuccess({
                    defaultViewConfig,
                    viewConfig,
                    fromPreset: !isSettingsEmpty,
                  });
                }),
              ),
            ),
            catchError((error) =>
              of(feedActions.fetchFeedViewConfigFail({ error })),
            ),
          ),
      ),
    ),
  );

  toggleFilter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.toggleFilter),
        withLatestFrom(
          this.store.select(selectFeed),
          this.store.select(getContentFilters),
        ),
        switchMap(([action, feed, contentFilters]) => {
          // the filer is not loaded yet
          if (action.filter.offset === -1) {
            this.store.dispatch(
              feedActions.loadFilter({
                feedId: feed.id,
                filter: action.filter,
                contentFilters,
                offset: 0,
              }),
            );
          }
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  loadFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.loadFilter),
      mergeMap((action) =>
        this.feedService
          .getFeedFilterItems(
            action.feedId,
            action.filter,
            action.contentFilters,
            action.offset,
          )
          .pipe(
            map((filterResponse) =>
              feedActions.loadFilterSuccess({ filterResponse }),
            ),
            catchError(() => EMPTY), // Here, you can dispatch a failure action if you want
          ),
      ),
    ),
  );

  /**
   * Load feed posts and widgets when period is changed
   */
  filtersChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          periodChanged,
          applyDefaultPeriod,
          selectPreviousPeriod,
          feedActions.activateFilters,
          feedActions.clearFilters,
          feedActions.deactivateFilterItem,
          feedActions.updateActiveFilterItem,
          feedActions.feedAttachDimensionsSuccess,
          feedActions.feedDetachDimensionSuccess,
          feedActions.editFeedSuccess,
          feedActions.searchPosts,
          feedActions.removePostFromFeedSuccess,
          feedActions.removeSelectedPostsFromFeedSuccess,
          feedActions.setDimensionJoinTypeSuccess,
          feedActions.deleteRemovedItemSuccess,
          feedActions.removeAllItemsBySource,
          feedActions.clearAllRemovedItems,
          feedActions.createAndAttachDimensionSuccess,
          feedActions.showSelectedPostsOnly,
        ),
        withLatestFrom(
          this.store.select(selectCurrentObject),
          this.store.select(selectFeed),
          this.store.select(selectWidgets),
          this.store.select(selectFeedFilters),
          this.store.select(getContentFilters),
          this.store.select(selectWidgetEditMode),
        ),
        switchMap(
          ([
            action,
            object,
            feed,
            widgets,
            filters,
            contentFilters,
            editMode,
          ]) => {
            if (feed) {
              this.store.dispatch(
                feedActions.loadFeedPosts({ feedId: object.id }),
              );
            }

            if (!editMode) {
              widgets.forEach((widget) => {
                if (
                  widget.is_loaded &&
                  widget.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER
                ) {
                  this.store.dispatch(
                    loadWidget({
                      widgetId: widget.id,
                    }),
                  );
                }
              });
            }

            filters.forEach((filter) => {
              // only the filters that are activated
              if (filter.offset !== -1) {
                this.store.dispatch(
                  feedActions.loadFilter({
                    feedId: object.id,
                    filter: filter,
                    contentFilters,
                    offset: 0,
                  }),
                );
              }
            });

            return EMPTY;
          },
        ),
      ),
    { dispatch: false },
  );

  $widgetsViewHasChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.changeWidgetsLanaguage, toggleDarkModeSuccess),
        withLatestFrom(
          this.store.select(selectWidgets),
          this.store.select(selectUser),
          this.store.select(selectWidgetEditMode),
        ),
        switchMap(([action, widgets, user, editMode]) => {
          if (!user || editMode) {
            return EMPTY; // do nothing if no user as dark mode can be changed on login screen
          }

          widgets.forEach((widget) => {
            if (widget.is_loaded) {
              this.store.dispatch(
                loadWidget({
                  widgetId: widget.id,
                }),
              );
            }
          });

          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  resultsChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          feedActions.pageChanged,
          feedActions.itemsPerPageChanged,
          feedActions.sortChanged,
          feedActions.sortDirectionChanged,
        ),
        withLatestFrom(this.store.select(selectFeed)),
        switchMap(([action, feed]) => {
          if (feed) {
            this.store.dispatch(feedActions.loadFeedPosts({ feedId: feed.id }));
          }

          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  attachDimensions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.feedAttachDimensions),
      switchMap((action) =>
        this.feedService
          .attachDimensions(
            action.feedId,
            action.dimensionIds,
            action.attachTypeId,
          )
          .pipe(
            map((response) =>
              feedActions.feedAttachDimensionsSuccess({
                dimensions: response.dimensions,
                query: response.query,
              }),
            ),
            catchError(() => EMPTY),
          ),
      ),
    ),
  );

  detachDimensions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.feedDetachDimension),

      switchMap((action) =>
        this.feedService
          .detachDimension(action.feedId, action.dimensionId)
          .pipe(
            map((feed) =>
              feedActions.feedDetachDimensionSuccess({
                dimensionId: action.dimensionId,
                query: feed.query,
              }),
            ),
            catchError(() => EMPTY),
          ),
      ),
    ),
  );

  toggleDimensionJoinType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.setDimensionJoinType),
      switchMap((action) =>
        this.feedService
          .setDimensionJoinType(
            action.feedId,
            action.dimensionId,
            action.joinTypeId,
          )
          .pipe(
            map((response: any) => {
              return feedActions.setDimensionJoinTypeSuccess({
                dimensionId: action.dimensionId,
                joinTypeId: action.joinTypeId,
                query: response.query,
              });
            }),
            catchError(() => EMPTY),
          ),
      ),
    ),
  );

  editFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.editFeed),
      switchMap((action) =>
        this.feedService
          .validateQuery(
            action.feedData.object_type_id,
            action.feedData.query_text,
            action.feedData.query_created_from,
            action.feedId,
          )
          .pipe(
            switchMap((validationResult) => {
              if ((validationResult as QueryValidationSuccess).success) {
                // Validation passed -> proceed to save
                return this.feedService
                  .editFeed(action.feedId, action.feedData)
                  .pipe(
                    switchMap((feed) => [
                      feedActions.editFeedSuccess({
                        feedId: action.feedId,
                        feed,
                      }),
                      submitFormSuccess({
                        entity: EditorFormEntity.Feed,
                        object: feed,
                        isCreated: false,
                      }),
                      feedActions.loadFeedCategoryTree({
                        feedCategoryIds: feed.company_categories.map(
                          (category: CompanyCategory) =>
                            category.company_category_id,
                        ),
                      }),
                    ]),
                    catchError((error) =>
                      of(
                        feedActions.editFeedFail(error),
                        submitFormFailure({
                          entity: EditorFormEntity.Feed,
                          error,
                        }),
                      ),
                    ),
                  );
              } else {
                // Validation failed -> dispatch form failure with validation message
                return of(
                  feedActions.editFeedFail(
                    validationResult as QueryValidationError,
                  ),
                  submitFormFailure({
                    entity: EditorFormEntity.Feed,
                    error: {
                      type: 'validation',
                      message: (validationResult as QueryValidationError)
                        .message,
                      line: (validationResult as QueryValidationError).line,
                      charPos: (validationResult as QueryValidationError)
                        .charPos,
                    },
                  }),
                );
              }
            }),
            catchError((error) => {
              return of(
                feedActions.editFeedFail(error),
                submitFormFailure({
                  entity: EditorFormEntity.Feed,
                  error: {
                    type: 'validation',
                    message: error?.response?.message,
                    line: error?.response?.line,
                    column: error?.response?.column,
                  },
                }),
              );
            }),
          ),
      ),
    ),
  );

  // ! Create and attach dimension in two steps
  createAndAttachDimension$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.createAndAttachDimension),
      withLatestFrom(this.store.select(selectFeed)),
      switchMap(([action, feed]) => {
        // * step 1: Create dimension using createFeed
        return this.feedService.createFeed(action.dimensionData).pipe(
          switchMap((dimension) => {
            const currentDimensions = feed.dimensions.map(
              (dimension) => dimension.id,
            );

            // * step 2: Attach dimension to feed
            return this.feedService
              .attachDimensions(
                action.attachParams.object_id,
                [...currentDimensions, dimension.id],
                action.attachParams.attach_type_id,
              )
              .pipe(
                switchMap((attachResponse) => [
                  // * update query and dimensions
                  feedActions.createAndAttachDimensionSuccess(),
                  feedActions.feedAttachDimensionsSuccess({
                    dimensions: attachResponse.dimensions,
                    query: attachResponse.query,
                  }),
                  submitFormSuccess({
                    entity: EditorFormEntity.Feed,
                    object: dimension,
                    isCreated: true,
                  }),
                ]),
                catchError((error) =>
                  of(
                    submitFormFailure({
                      entity: EditorFormEntity.Feed,
                      error: 'Failed to attach dimension',
                    }),
                  ),
                ),
              );
          }),
          catchError((error) =>
            of(
              submitFormFailure({
                entity: EditorFormEntity.Feed,
                error: 'Failed to create dimension',
              }),
            ),
          ),
        );
      }),
    ),
  );

  attachDimensionSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.feedAttachDimensionsSuccess),
        tap((action) => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'feed.toasts.attached-dimensions-updated',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  createFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.createFeed),
      switchMap((action) =>
        this.feedService
          .validateQuery(
            action.feedData.object_type_id,
            action.feedData.query_text,
            action.feedData.query_created_from,
          )
          .pipe(
            switchMap((validationResult) => {
              if ((validationResult as QueryValidationSuccess).success) {
                return this.feedService.createFeed(action.feedData).pipe(
                  switchMap((feed) => [
                    feedActions.createFeedSuccess({ feed }),
                    submitFormSuccess({
                      entity: EditorFormEntity.Feed,
                      object: feed,
                      isCreated: true,
                    }),
                  ]),
                  catchError((error) =>
                    of(
                      feedActions.createFeedFail(error),
                      submitFormFailure({
                        entity: EditorFormEntity.Feed,
                        error,
                      }),
                    ),
                  ),
                );
              }
              return null;
            }),
            catchError((error) =>
              of(
                feedActions.createFeedFail(error),
                submitFormFailure({
                  entity: EditorFormEntity.Feed,
                  error: {
                    type: 'validation',
                    message: error?.response?.message,
                    line: error?.response?.line,
                    column: error?.response?.column,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  downloadObject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.downloadObject),
      switchMap((action) =>
        this.store.select(selectObjectById(action.objectId)).pipe(
          withLatestFrom(this.store.select(getContentFilters)),
          take(1),
          switchMap(([object, contentFilters]) =>
            this.feedService
              .downloadObject(
                object,
                contentFilters,
                action.formats,
                action.languages,
                action.outputSettingId,
              )
              .pipe(
                switchMap((response) => [
                  feedActions.downloadObjectSuccess({ response }), // Entity-specific success action
                  submitFormSuccess({
                    entity: EditorFormEntity.ExportForm,
                    object: response,
                    isCreated: false,
                  }), // Generic form success action
                ]),
                catchError((error) =>
                  of(
                    feedActions.downloadObjectFail(), // Entity-specific failure action
                    submitFormFailure({
                      entity: EditorFormEntity.ExportForm,
                      error,
                    }), // Generic form failure action
                  ),
                ),
              ),
          ),
        ),
      ),
    ),
  );

  loadAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.loadFeedAlerts),
      switchMap((action) =>
        this.alertsService.fetchAlerts(NomObjectClass.FEED, action.feedId).pipe(
          map((alerts) => feedActions.loadFeedAlertsSuccess({ alerts })),
          catchError((error) => of(feedActions.loadFeedAlertsFail())), // Here, you can dispatch a failure action if you want
        ),
      ),
    ),
  );

  loadFeedAlgorithms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.loadFeedAlgorithms),
      switchMap((action) =>
        this.feedService.getFeedAlgorithms(action.feedId).pipe(
          map((algorithms) =>
            feedActions.loadFeedAlgorithmsSuccess({ algorithms }),
          ),
          catchError((error) =>
            of(feedActions.loadFeedAlgorithmsFail({ error })),
          ),
        ),
      ),
    ),
  );

  detachFeedAlgorithm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.detachFeedAlgorithm),
      switchMap((action) =>
        this.feedService
          .detachFeedAlgorithm(
            action.feedId,
            action.algorithmId,
            action.scheduleId,
          )
          .pipe(
            map(() =>
              feedActions.detachFeedAlgorithmSuccess({
                feedId: action.feedId,
                algorithmId: action.algorithmId,
              }),
            ),
            catchError((error) => {
              this.toastService.showError(
                this.translateService.instant(
                  'feed-algorithms.message.detach-failed',
                ),
              );
              return of(
                feedActions.detachFeedAlgorithmFail({
                  algorithmId: action.algorithmId,
                  error,
                }),
              );
            }),
          ),
      ),
    ),
  );

  detachFeedAlgorithmSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.detachFeedAlgorithmSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'feed-algorithms.message.algorithm-detached-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  updateFeedAlgorithmParams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.updateFeedAlgorithmParams),
      switchMap((action) =>
        this.feedService
          .updateFeedAlgorithmParams(
            action.feedId,
            action.algorithmId,
            action.params,
            action.scheduleId,
          )
          .pipe(
            map(() =>
              feedActions.updateFeedAlgorithmParamsSuccess({
                feedId: action.feedId,
                algorithmId: action.algorithmId,
              }),
            ),
            catchError((error) => {
              this.toastService.showError(
                this.translateService.instant(
                  'feed-algorithms.message.update-params-failed',
                ),
              );
              return of(
                feedActions.updateFeedAlgorithmParamsFail({
                  algorithmId: action.algorithmId,
                  error,
                }),
              );
            }),
          ),
      ),
    ),
  );

  updateFeedAlgorithmParamsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.updateFeedAlgorithmParamsSuccess),
      map(({ feedId }) => feedActions.loadFeedAlgorithms({ feedId })),
    ),
  );

  updateFeedAlgorithmParamsSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.updateFeedAlgorithmParamsSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'feed-algorithms.message.algorithm-settings-updated-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  clearSelectedPosts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.clearFeedSelectedItems),
      withLatestFrom(this.store.select(selectUser)),
      switchMap(([action, user]) =>
        this.postsService.clearFeedSelectedItems(user.id, action.feedId).pipe(
          map((response) =>
            feedActions.clearFeedSelectedItemsSuccess({
              feedId: action.feedId,
            }),
          ),
          catchError((error) =>
            of(feedActions.clearFeedSelectedItemsFail(error)),
          ), // Here, you can dispatch a failure action if you want
        ),
      ),
    ),
  );

  exportSelectedPosts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.exportSelectedPosts),
      withLatestFrom(this.store.select(selectUser)),
      switchMap(([action, user]) =>
        this.feedService
          .exportSelectedPosts(user.id, action.feedId, action.format)
          .pipe(
            // Filter out responses with empty download_url
            filter(
              (response: any) =>
                !!response.download_url && response.download_url.trim() !== '',
            ),
            map((response: any) =>
              feedActions.exportSelectedPostsSuccess({
                downloadUrl: response.download_url,
              }),
            ),
            catchError((error) =>
              of(feedActions.exportSelectedPostsFail(error)),
            ),
          ),
      ),
    ),
  );

  shareSelectedPostsToClippingFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.shareSelectedToClippingFeed),
      withLatestFrom(this.store.select(selectUser)),
      switchMap(([action, user]) =>
        this.feedService
          .shareSelectedPostsToFeeds(action.feedId, action.sharingFeedIds)
          .pipe(
            map((response) => feedActions.shareSelectedToClippingFeedSuccess()),
            catchError((error) =>
              of(feedActions.shareSelectedToClippingFeedFail(error)),
            ), // Here, you can dispatch a failure action if you want
          ),
      ),
    ),
  );

  shareSelectedPostsToClippingFeedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.shareSelectedToClippingFeedSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'feed.toasts.posts-shared-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  loadUserCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserCategories),
      switchMap((action) =>
        this.postsService
          .getPostCategories(action.postIds, action.selectedPostsFeedId)
          .pipe(
            map((userCategories) =>
              loadUserCategoriesSuccess({ userCategories }),
            ),
            catchError((error) => of(loadUserCategoriesFail({ error }))), // Here, you can dispatch a failure action if you want
          ),
      ),
    ),
  );

  postRemoveFromFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.removePostFromFeed),
      withLatestFrom(this.store.select(selectUser)),
      switchMap(([action, user]) =>
        this.feedService.removeFromFeed(action.postId, action.feedId).pipe(
          map((removedItems) =>
            feedActions.removePostFromFeedSuccess({ removedItems }),
          ),
          catchError((error) =>
            of(feedActions.removePostFromFeedFail({ error })),
          ), // Here, you can dispatch a failure action if you want
        ),
      ),
    ),
  );

  postRemoveFromFeedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.removePostFromFeedSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'feed.toasts.post-removed-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  postRemoveFromFeedFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.removePostFromFeedFail),
        tap(() => {
          this.toastService.showError(
            this.translateService.instant('feed.toasts.post-remove-failed'),
          );
        }),
      ),
    { dispatch: false },
  );

  // * Effect that applies a feed post algorithm.
  applyFeedPostsAlgorithm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.applyFeedPostAlgorithm),
      withLatestFrom(
        this.store.select(getContentFilters),
        this.store.select(selectFeed),
      ),
      mergeMap(([action, contentFilters, feed]) =>
        this.feedService
          .applyFeedPostsAlgorithm(
            action.feedId,
            action.algorithmId,
            action.contentFilters ?? contentFilters,
            action.algorithmParams,
          )
          .pipe(
            map((userBackgroundTask) =>
              feedActions.applyFeedPostAlgorithmSuccess({
                userBackgroundTask,
                feedId: action.feedId,
                scheduled: !!action.algorithmParams?.schedule,
              }),
            ),
            catchError((error) =>
              of(feedActions.applyFeedPostAlgorithmFail({ error })),
            ), // Here, you can dispatch a failure action if you want
          ),
      ),
    ),
  );

  applyFeedPostsAlgorithmSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.applyFeedPostAlgorithmSuccess),
        tap(({ scheduled }) => {
          this.toastService.showSuccess(
            this.translateService.instant(
              scheduled
                ? 'feed.toasts.algorithm-scheduled-successfully'
                : 'feed.toasts.algorithm-applied-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  /**
   * Scheduling an algorithm attaches it to the feed as part of the same
   * request, so the Scheduled Algorithms pane and its sidebar counter are
   * stale the moment the dialog closes. Re-fetch the attached list.
   */
  reloadAlgorithmsAfterSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.applyFeedPostAlgorithmSuccess),
      filter(({ scheduled }) => scheduled),
      map(({ feedId }) => feedActions.loadFeedAlgorithms({ feedId })),
    ),
  );

  feedCreateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.createFeedSuccess),
        tap((action) => {
          const messageKey =
            action.feed.object_type_id != NomObjectTypes.SEARCH_DIMENSION
              ? 'feed.toasts.feed-created-successfully'
              : 'feed.toasts.dimension-created-successfully';
          this.toastService.showSuccess(
            this.translateService.instant(messageKey),
          );

          // Navigate to the newly created feed
          if (action.feed.object_type_id != NomObjectTypes.SEARCH_DIMENSION) {
            // navigate to path: 'workspace/:workspaceId/feed/:feedId',
            this.router.navigate([
              'analytics',
              'workspace',
              action.feed.workspace_id,
              'feed',
              action.feed.id,
            ]);
          } else {
            // navigate to path: 'workspace/:workspaceId/clipping-feed/:feedId',
            this.router.navigate([
              'analytics',
              'workspace',
              action.feed.workspace_id,
            ]);
          }
        }),
      ),
    { dispatch: false },
  );

  feedEditFeedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.editFeedSuccess),
        tap((action) => {
          const messageKey =
            action.feed.object_type_id != NomObjectTypes.SEARCH_DIMENSION
              ? 'feed.toasts.feed-edited-successfully'
              : 'feed.toasts.dimension-edited-successfully';
          this.toastService.showSuccess(
            this.translateService.instant(messageKey),
          );
        }),
      ),
    { dispatch: false },
  );

  periodChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(periodChanged),
        tap((action) => {
          this.updateQueryParamsSilently({
            start_time: action.period.startTimestamp,
            end_time: action.period.endTimestamp,
          });
        }),
      ),
    { dispatch: false },
  );

  defaultPeriodChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(applyDefaultPeriod),
        tap((action) => {
          this.updateQueryParamsSilently({
            start_time: null,
            end_time: null,
          });
        }),
      ),
    { dispatch: false },
  );

  togglePeriodLockedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(togglePeriodLockedSuccess),
        tap(({ isPeriodLocked }) => {
          if (isPeriodLocked) {
            this.updateQueryParamsSilently({
              start_time: null,
              end_time: null,
            });
          }
        }),
      ),
    { dispatch: false },
  );

  pageChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.pageChanged),
        tap((action) => {
          this.updateQueryParamsSilently({
            page: action.pageNumber,
          });
        }),
      ),
    { dispatch: false },
  );

  filtersChangedUpdateUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.activateFilters),
        withLatestFrom(this.store.select(selectFeedActiveFilterItems)),
        tap(([action, activeFilterItems]) => {
          const filters = activeFilterItems.map(
            (filter) => filter.property_uri,
          );
          // TODO: this will be uncommented futher - related to filter in the url state
          // this.updateQueryParamsSilently({
          //   filters,
          // });
        }),
      ),
    { dispatch: false },
  );

  duplicateFeeds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.duplicateFeeds),
      switchMap((action) =>
        this.feedService.duplicateFeeds(action.copyFeedParams).pipe(
          map((response) => {
            if ('error' in response) {
              const errors: DuplicateObjectErrorItem[] = Object.values(
                response.errors,
              ).sort((a, b) => a.display_order - b.display_order);

              return feedActions.duplicateFeedsConstraintError({ errors });
            }

            return feedActions.duplicateFeedsSuccess({
              response: response.object,
            });
          }),
          catchError((error) => of(feedActions.duplicateFeedsFail({ error }))),
        ),
      ),
    ),
  );

  duplicateFeedsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.duplicateFeedsSuccess),
        tap((action) => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'feed.toasts.feed-duplicated-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  duplicateFeedsConstraintError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.duplicateFeedsConstraintError),
        tap((action) => {
          this.dialogService.openSimpleDialog(
            BusinessConstraintsInfoDialogComponent,
            {
              data: {
                errors: action.errors,
              },
              closeOnClick: false,
            },
          );
        }),
      ),
    { dispatch: false },
  );
  private feedPresetService: any;

  /**
   * Updates the query parameters silently by creating a new URL tree with the provided parameters
   * and updating the URL without adding a history entry.
   *
   * @param params - The query parameters to be updated.
   */
  updateQueryParamsSilently(params: { [key: string]: any }) {
    // First, get the current router state
    const currentRoute = this.router.routerState.snapshot.root;

    // Then, extract the current query parameters
    let currentQueryParams = {};
    if (currentRoute) {
      currentQueryParams = currentRoute.queryParams;
    }

    // Merge the current query parameters with the new ones
    const mergedQueryParams = { ...currentQueryParams, ...params };

    // Now, create a URL tree with the merged query parameters
    const url = this.router
      .createUrlTree([], { queryParams: mergedQueryParams })
      .toString();

    // Use replaceState to update the URL without adding a history entry
    this.location.replaceState(url);
  }

  removeSelectedPostsFromFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.removeSelectedPostsFromFeed),
      switchMap((action) =>
        this.feedService
          .removeSelectedFromFeed(action.feedId, action.applytoChildObjects)
          .pipe(
            map((removedItems) =>
              feedActions.removeSelectedPostsFromFeedSuccess({
                feedId: action.feedId,
                removedItems: removedItems,
              }),
            ),
            catchError((error) =>
              of(feedActions.removeSelectedPostsFromFeedFail({ error })),
            ),
          ),
      ),
    ),
  );

  getFeedHistoryRecords$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.getFeedHistory),
      switchMap((action) =>
        this.feedService.getFeedHistory(action.feedId).pipe(
          map((query_history_records) =>
            feedActions.getFeedHistorySuccess({ query_history_records }),
          ),
          catchError((error) => of(feedActions.getFeedHistoryFail({ error }))),
        ),
      ),
    ),
  );

  toggleFilterDimension$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.toggleFilterDimension),
      filter((action) => action.activate === true),
      map(() => feedActions.activateFilters()),
    ),
  );

  selectFilterItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.selectFilterItem),
      concatLatestFrom(() => this.store.select(selectFeedFilters)),
      switchMap(([{ filterItem, mode }, filters]) => {
        const existingFilter = findFilter(filters, filterItem);

        // Case 1: found in loaded filters – just reuse its meta
        if (existingFilter) {
          const resolvedItem: AnyFilterItem = {
            ...filterItem,
            type: mode,
            filter_title: existingFilter.name,
          };

          return of(
            feedActions.selectFilterItemResolved({ item: resolvedItem }),
          );
        }

        // Case 2: not found – call backend
        let request$: Observable<FilterItemResolveResponse>;

        request$ = this.feedService.getValueFilterItemMeta(
          filterItem.property_uri,
          (filterItem as FilterItem).raw_value,
        );

        return request$.pipe(
          switchMap((response) => {
            const resolvedItem: AnyFilterItem = {
              ...filterItem,
              type: mode,
              title: response.property_value_title ?? filterItem.title,
              filter_title: response.property_title ?? filterItem.filter_title,
            };

            return of(
              feedActions.selectFilterItemResolved({ item: resolvedItem }),
            );
          }),
          catchError((error) =>
            of(feedActions.selectFilterItemResolveFailed({ error })),
          ),
        );
      }),
    ),
  );

  fetchFeedOperators$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.fetchFeedOperators),
      switchMap((action) =>
        this.feedService.getFeedOperatorHints(action.objectTypeId).pipe(
          map((feedOperators) =>
            feedActions.fetchFeedOperatorsSuccess({ feedOperators }),
          ),
          catchError((error) =>
            of(feedActions.fetchFeedOperatorsFail({ error })),
          ),
        ),
      ),
    ),
  );

  createAlert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.createAlert),
      switchMap((action) =>
        this.alertsService.createAlert(action.feedId, action.alert).pipe(
          switchMap((alert) => [
            feedActions.createAlertSuccess({ alert }), // Entity-specific success action
            submitFormSuccess({
              entity: EditorFormEntity.Alert,
              object: alert,
              isCreated: true,
            }), // Generic form success action
          ]),
          catchError((error) =>
            of(
              feedActions.createAlertFail({ error }), // Entity-specific failure action
              submitFormFailure({ entity: EditorFormEntity.Alert, error }), // Generic form failure action
            ),
          ),
        ),
      ),
    ),
  );

  editAlert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.editAlert),
      switchMap((action) =>
        this.alertsService.editAlert(action.alertId, action.alert).pipe(
          switchMap((alert) => [
            feedActions.editAlertSuccess({ alert }), // Entity-specific success action
            submitFormSuccess({
              entity: EditorFormEntity.Alert,
              object: alert,
              isCreated: false,
            }), // Generic form success action
          ]),
          catchError((error) =>
            of(
              feedActions.editAlertFail({ error }), // Entity-specific failure action
              submitFormFailure({ entity: EditorFormEntity.Alert, error }), // Generic form failure action
            ),
          ),
        ),
      ),
    ),
  );

  editAlertSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.editAlertSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'feed.toasts.alert-edited-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  createAlertSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.createAlertSuccess),
        tap(() => {
          this.toastService.showSuccess(
            this.translateService.instant(
              'feed.toasts.alert-created-successfully',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  deleteAlert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.deleteAlert),
      switchMap((action) =>
        this.alertsService.deleteAlert(action.alertId).pipe(
          map(() =>
            feedActions.deleteAlertSuccess({ alertId: action.alertId }),
          ),
          catchError((error) => of(feedActions.deleteAlertFail({ error }))),
        ),
      ),
    ),
  );

  toggleAlertMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.toggleAlertMode),
      switchMap((action) =>
        this.alertsService.toggleAlertMode(action.alertId).pipe(
          map(() =>
            feedActions.toggleAlertModeSuccess({ alertId: action.alertId }),
          ),
          catchError((error) => of(feedActions.toggleAlertModeFail({ error }))),
        ),
      ),
    ),
  );

  toggleAlertModeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.toggleAlertModeSuccess),
        tap(() =>
          this.toastService.showSuccess(
            this.translateService.instant(
              'feed.toasts.alert-mode-changed-successfully',
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  toggleAlertModeFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.toggleAlertModeFail),
        tap(() => {
          this.toastService.showError(
            this.translateService.instant(
              'feed.toasts.alert-mode-change-failed',
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  toggleFeedView$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.toggleFeedView),
        withLatestFrom(this.store.select(selectFeed)),
        switchMap(([action, feed]) =>
          this.feedService.saveFeedViewsState(feed.id, feed.views).pipe(
            map(
              (response) => {},
              //feedActions.toggleFeedViewSuccess({ view: action.view })
            ),
            catchError((error) =>
              of(feedActions.toggleFeedViewFail({ error })),
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  setFeedPostsViewMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.setFeedPostViewMode),
      withLatestFrom(this.store.select(selectFeed)),
      switchMap(([action, feed]) =>
        this.feedService.setFeedPostViewMode(feed.id, action.viewMode).pipe(
          map(() =>
            feedActions.setFeedPostViewModeSuccess({
              viewMode: action.viewMode,
            }),
          ),
          catchError((error) =>
            of(feedActions.setFeedPostViewModeFail({ error })),
          ),
        ),
      ),
    ),
  );

  saveOutputSettingsState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveObjectExportSettingsState),
      withLatestFrom(this.store.select(selectFeed)),
      switchMap(([action, feed]) =>
        this.outputSettings
          .saveObjectExportSettingsState(
            action.contextObject,
            action.settingsType,
            action.settings,
          )
          .pipe(
            map(() =>
              saveObjectOutputSettingsStateSuccess({
                objectId: action.contextObject.id,
                objectClassId: action.contextObject.object_class_id,
                settingsType: action.settingsType,
                settings: action.settings,
              }),
            ),
            catchError((error) =>
              of(saveObjectOutputSettingsStateFail({ error })),
            ),
          ),
      ),
    ),
  );

  loadAvailableCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.loadAvailableCategories),
      switchMap((action) =>
        this.feedService.fetchUserCategories(action.feedId).pipe(
          map((categories) =>
            feedActions.loadAvailableCategoriesSuccess({ categories }),
          ),
          catchError((error) =>
            of(feedActions.loadAvailableCategoriesFail({ error })),
          ),
        ),
      ),
    ),
  );

  assignFeedCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.assignFeedCategory),
      switchMap((action) =>
        this.feedService
          .assignFeedCategory(action.feedId, action.categoryIds)
          .pipe(
            map((categories) =>
              feedActions.assignFeedCategorySuccess({ categories }),
            ),
            catchError((error) =>
              of(feedActions.assignFeedCategoryFail({ error })),
            ),
          ),
      ),
    ),
  );

  loadFeedCategoryTree$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.loadFeedCategoryTree),
      switchMap((action) =>
        this.feedService.fetchUserCategoryTree(action.feedCategoryIds).pipe(
          map((feedCategoryTree) =>
            feedActions.loadFeedCategoryTreeSuccess({
              categoryTree: feedCategoryTree,
            }),
          ),
          catchError((error) =>
            of(feedActions.loadFeedCategoryTreeFail({ error })),
          ),
        ),
      ),
    ),
  );

  saveFeedSettings$ = createEffect(() =>
    this.store.select(selectFeed).pipe(
      // Ensure we actually have a feed before listening to settings
      filter((feed): feed is NonNullable<typeof feed> => !!feed?.id),
      distinctUntilChanged((a, b) => isEqual(a.id, b.id)),
      // Switch to the settings stream for THIS specific feed
      switchMap((feed) =>
        this.store.select(selectFeedSettings).pipe(
          // Skip the very first emission that happens when the feed loads
          skip(1),
          distinctUntilChanged((a, b) => isEqual(a, b)),
          switchMap((feedSettings) =>
            this.feedService.saveFeedSettings(feed.id, feedSettings).pipe(
              map((response) =>
                feedActions.saveFeedSettingsSuccess({ response }),
              ),
              catchError((error) =>
                of(feedActions.saveFeedSettingsFail({ error })),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  fetchRemovedItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.fetchRemovedItems),
      switchMap((action) =>
        this.feedService.requestGetRemovedItems(action.object).pipe(
          map((removedItems: FeedRemovedItem[]) =>
            feedActions.fetchRemovedItemsSuccess({ removedItems }),
          ),
          catchError((error) =>
            of(feedActions.fetchRemovedItemsFail({ error })),
          ),
        ),
      ),
    ),
  );

  deleteRemovedItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.deleteRemovedItem),
      switchMap((action) =>
        this.feedService
          .requestDeleteRemovedItem(
            action.feedId,
            action.objectId,
            action.objectClassId,
          )
          .pipe(
            map(() =>
              feedActions.deleteRemovedItemSuccess({
                objectId: action.objectId,
              }),
            ),
            catchError((error) =>
              of(feedActions.deleteRemovedItemFail({ error })),
            ),
          ),
      ),
    ),
  );

  deleteAllRemovedItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.clearAllRemovedItems),
      switchMap((action) =>
        this.feedService.clearRemovedFilters(action.object).pipe(
          map(() => feedActions.clearAllRemovedItemsSuccess()),
          catchError((error) =>
            of(feedActions.clearAllRemovedItemsItemsFail({ error })),
          ),
        ),
      ),
    ),
  );

  removeAllItemsBySource$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.removeAllItemsBySource),
      switchMap((action) =>
        this.feedService
          .requestRemoveItems(
            action.feedId,
            action.objectId,
            action.objectClassId,
            true,
          )
          .pipe(
            map((removedItems: FeedRemovedItem[]) =>
              feedActions.removeAllItemsBySourceSuccess({ removedItems }),
            ),
            catchError((error) =>
              of(feedActions.removeAllItemsBySourceFail({ error })),
            ),
          ),
      ),
    ),
  );

  fetchWorkflows$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.fetchWorkflows),
      switchMap((action) =>
        this.nomService.fetchWorkflows().pipe(
          map((workflows) => feedActions.fetchWorkflowsSuccess({ workflows })),
          catchError((error) => of(feedActions.fetchWorkflowsFail({ error }))),
        ),
      ),
    ),
  );

  toggleCardViewConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.toggleViewConfigOption),
      withLatestFrom(
        this.store.select(selectFeedViewConfig),
        this.store.select(selectFeed),
      ),
      switchMap(([action, viewConfig, feed]) => {
        const viewPreset: ViewPreset = {
          view_id: ViewId.PaneViewFeedResults,
          name: '', // or use action.name if it's a "Save As" flow
          settings: viewConfig,
          context_object_id: feed.id,
          context_object_class_id: NomObjectClass.FEED,
        };

        viewPreset.settings = viewPreset.settings.map((configItem) => {
          if (
            configItem.radioGroup === 'post_time_mode' &&
            configItem.label !== action.optionName
          ) {
            return { ...configItem, selected: false };
          }
          return configItem;
        });

        return this.viewPresetService.saveViewPreset(viewPreset, false).pipe(
          map((savedPreset) => feedActions.toggleViewConfigOptionSuccess()),
          catchError((error) =>
            of(feedActions.toggleViewConfigOptionFail({ error })),
          ),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private feedService: FeedService,
    private viewPresetService: ViewPresetService,
    private alertsService: AlertService,
    private postsService: PostService,
    private nomService: NomenclatureService,
    private store: Store,
    private toastService: ToastService,
    private router: Router,
    private location: Location,
    private feedSettings: FeedSettingsService,
    private widgetSettings: WidgetSettingsService,
    private outputSettings: OutputSettingsService,
    private translateService: TranslateService,
    private dialogService: DialogService,
  ) {}
}
